
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import Footer from '../../home/components/Footer';

export default function AbbotsLangleyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Electrician in Abbots Langley | Beyond Electrical Watford';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Trusted electrician serving Abbots Langley and surrounding areas. NICEIC approved, 15 years experience. Call 020 3198 1818 for same-day service.');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://beyondelectrical.co.uk/electrician-abbots-langley');
    }
  }, []);

  const services = [
    { name: 'Electrical Installations', link: '/services/installations/electrical-installations', icon: 'ri-flashlight-line' },
    { name: 'Full & Partial Rewiring', link: '/services/installations/rewiring', icon: 'ri-git-branch-line' },
    { name: 'Consumer Unit Upgrades', link: '/services/installations/consumer-unit-upgrades', icon: 'ri-dashboard-line' },
    { name: 'EV Charger Installation', link: '/services/installations/ev-charger', icon: 'ri-charging-pile-2-line' },
    { name: 'EICR Testing', link: '/services/safety-compliance/eicr-testing', icon: 'ri-file-list-3-line' },
    { name: 'Fault Finding & Repairs', link: '/services/repairs/fault-finding', icon: 'ri-search-line' },
    { name: 'Lighting Installation', link: '/services/installations/lighting-installation', icon: 'ri-lightbulb-line' },
    { name: 'Smart Home Systems', link: '/services/installations/smart-home', icon: 'ri-home-smile-line' },
    { name: 'Emergency Callouts', link: '/services/repairs/emergency-callouts', icon: 'ri-alarm-warning-line' },
  ];

  const whyChoose = [
    { title: '15 Years Experience', description: 'Trusted expertise in all electrical work', icon: 'ri-medal-line' },
    { title: 'NICEIC Approved', description: 'Fully certified and insured', icon: 'ri-shield-check-line' },
    { title: 'Same Day Service', description: 'Fast response for urgent jobs', icon: 'ri-time-line' },
    { title: 'Fixed Pricing', description: 'No hidden costs or surprises', icon: 'ri-price-tag-3-line' },
  ];

  const surroundingAreas = ['Watford', 'Kings Langley', 'Hemel Hempstead', 'Rickmansworth', 'Chorleywood', 'Croxley Green', 'Bushey', 'Radlett'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm border border-white/20">
              <i className="ri-shield-check-line text-amber-400"></i>
              NICEIC Approved Contractor
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm border border-white/20">
              <i className="ri-map-pin-line text-amber-400"></i>
              Local to Abbots Langley
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Electrician in <span className="text-amber-400">Abbots Langley</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl leading-relaxed">
            Professional electrical services for homes and businesses in Abbots Langley. From installations to emergency repairs, we deliver safe, reliable solutions with 15 years of trusted expertise.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-400 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-amber-500 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line text-xl"></i>
              Call 020 3198 1818
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap cursor-pointer">
              <i className="ri-mail-line text-xl"></i>
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services in Abbots Langley</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Comprehensive electrical solutions for residential and commercial properties</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link key={index} to={service.link} className="group bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-amber-400 hover:shadow-xl transition-all cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4 group-hover:bg-amber-400 transition-colors">
                  <i className={`${service.icon} text-3xl text-amber-400 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-400 transition-colors">{service.name}</h3>
                <div className="flex items-center text-amber-400 font-semibold mt-4">
                  Learn More <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Your trusted local electrician in Abbots Langley</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-4xl text-amber-400`}></i>
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Areas We Cover</h2>
            <p className="text-xl text-gray-600">Serving Abbots Langley and surrounding areas</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {surroundingAreas.map((area, index) => (
              <span key={index} className="bg-white px-6 py-3 rounded-full text-gray-700 font-medium border-2 border-gray-200 hover:border-amber-400 hover:text-amber-400 transition-colors cursor-pointer">{area}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Need an Electrician in Abbots Langley?</h2>
          <p className="text-xl text-gray-600 mb-8">Contact us today for fast, professional electrical services</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-400 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-amber-500 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line text-xl"></i>
              Call 020 3198 1818
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-mail-line text-xl"></i>
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
